import React from 'react'

// Components
import { Item, SubMenu } from '../../../components/SelectDropdown'

import { HeaderItem } from '../MoveShareToProject.styles'
import {
  ErrorState,
  LoadingState,
  WorkspacesItem,
  Chevron,
  SignedOutLabel,
  WorkspaceLogo,
} from './OtherWorkspacesMenu.styles'

// Icons
import { ReactComponent as AllDocumentsIcon } from '@sketch/icons/documents-prism-16-svg'
import { ReactComponent as WorkspaceIcon } from '@sketch/icons/workspace-16-svg'
import { ReactComponent as FolderClosed } from '@sketch/icons/folder-closed-16-svg'
import { ReactComponent as RestrictedProject } from '@sketch/icons/lock-16-svg'

// GraphQL
import { ProjectFragment, WorkspaceMinimalFragment } from '@sketch/gql-types'
import { useGetProjectsLazy } from 'modules/projects/operations'
import { useUserAuthorizations } from 'modules/sso/operations'

const allDocumentsProject: ProjectFragment = {
  __typename: 'Project',
  name: 'All Documents',
  identifier: '',
  type: 'STANDARD',
  deletedAt: null,
  workspaceAccessLevel: 'DEFAULT',
}

// Helpers
const getProjectIcon = (project: ProjectFragment) => {
  const isProjectRestricted = project.workspaceAccessLevel === 'NONE'

  // This equality works because we are appending the "allDocumentsProject" in
  // WorkspaceProjectsList (down below):
  // ---> "items = [allDocumentsProject, ...(projects?.entries || [])]"
  const isAllDocumentsProject = project === allDocumentsProject

  // Lock Icon
  if (isProjectRestricted) {
    return RestrictedProject
  }

  // All Documents Icon
  if (isAllDocumentsProject) {
    return AllDocumentsIcon
  }

  // Regular project, a closed folder icon
  return FolderClosed
}

// Types
type AdditionalItems = 'loading' | 'error'

interface OtherWorkspacesMenuProps {
  workspaces: WorkspaceMinimalFragment[]
}

export function OtherWorkspacesMenu(props: OtherWorkspacesMenuProps) {
  const { workspaces } = props

  if (workspaces.length === 0) {
    return null
  }

  return (
    <SubMenu
      items={workspaces}
      headerItem={<HeaderItem>Other Workspaces</HeaderItem>}
      renderTrigger={({ active, ...props }) => (
        <Item
          active={active}
          text="Other Workspaces"
          icon={WorkspaceIcon}
          additional={<Chevron />}
          {...props}
        />
      )}
      renderItem={workspace => <WorkspaceProjectsList workspace={workspace} />}
      value="Other Workspaces"
    />
  )
}

interface WorkspaceProjectsListProps {
  workspace: WorkspaceMinimalFragment
}

const WorkspaceProjectsList = function (props: WorkspaceProjectsListProps) {
  const { workspace } = props
  const { projects, loading, error, getProjects } = useGetProjectsLazy()
  const { hasAccessToWorkspace } = useUserAuthorizations()

  let items: (ProjectFragment | AdditionalItems)[] = ['loading']

  if (loading) {
    items = ['loading' as const]
  } else if (error) {
    items = ['error' as const]
  } else {
    items = [allDocumentsProject, ...(projects?.entries || [])]
  }

  const workspaceSignOut = !hasAccessToWorkspace(workspace)
  const workspaceDisabled = workspaceSignOut || workspace.status !== 'ACTIVE'

  let additionalIcon: React.ReactElement | null = null

  if (workspaceSignOut) {
    additionalIcon = <SignedOutLabel expired={false} />
  } else if (!workspaceDisabled) {
    additionalIcon = <Chevron />
  }

  return (
    <SubMenu
      disabled={workspaceDisabled}
      headerItem={<HeaderItem>{workspace.name}</HeaderItem>}
      renderTrigger={props => (
        <WorkspacesItem
          disabled={workspaceDisabled}
          text={workspace.name}
          icon={({ className }) => (
            <WorkspaceLogo
              className={className}
              workspaceName={workspace.name}
              src={workspace.avatar?.large}
              size="24px"
            />
          )}
          additional={additionalIcon}
          onMouseEnter={() => {
            getProjects({
              variables: { workspaceId: workspace.identifier },
            })
          }}
          {...props}
        />
      )}
      items={items}
      value={workspace}
      renderItem={(item, props) => {
        const projectItem = item as ProjectFragment
        const icon = getProjectIcon(projectItem)

        if (typeof item === 'object') {
          return (
            <Item
              active={props.active}
              icon={icon}
              text={item.name}
              onClick={props.onClick}
            />
          )
        } else if (item === 'error') {
          return <ErrorState />
        } else {
          return <LoadingState />
        }
      }}
    />
  )
}
